// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Type definitions for Terraviz project
 */

import type { ColorScale, RenderEncoding } from './color-scale'
import type { DisplayColorScale } from './unit-scale'
import type { ColorKey } from '../services/colorKey'
export type { ColorScale, DisplayColorScale, RenderEncoding }

/**
 * Supported dataset formats.
 *
 * The canonical set mirrors the publisher API's `FORMAT_VALUES`
 * allow-list (`functions/api/v1/_lib/validators.ts`):
 * `video/mp4`, `application/dash+xml`, `image/png`, `image/jpeg`, `image/webp`.
 * Anything a publisher can upload, the SPA can render. AYNI
 * (2026-10-07): `khipu/story` is a NOAA catalogue row whose guided
 * sequence became a story Khipu (`dataService.withStories`).
 *
 * `image/jpg` and `images/jpg` are the legacy SOS-catalog typos
 * (preserved verbatim in the upstream JSON). They're normalised
 * to `image/jpeg` at the source-fetch boundary by
 * `dataService.normaliseSourceFormat` (Phase 1f/L), so post-
 * normalisation SPA code only ever sees `image/jpeg`. They stay
 * in the union as defensive types in case a fork bypasses the
 * normaliser; the renderer tolerates them too (Phase 1f/K).
 */
export type DatasetFormat =
  | 'video/mp4'
  | 'application/dash+xml'
  | 'image/png'
  | 'image/jpeg'
  | 'image/webp'
  | 'image/jpg'
  | 'images/jpg'
  | 'khipu/story'
  // AYNI: places on the globe that each play something live (services/geoMedia.ts).
  | 'geo-media/json'

/**
 * Core dataset metadata from SOS API
 */
export interface Dataset {
  id: string
  /**
   * Bulk-import provenance set by Phase 1d's `terraviz import-snapshot`
   * to the SOS snapshot's internal id (e.g. `INTERNAL_SOS_768`).
   * `getDatasetById` falls back to matching this field when a primary
   * `id` lookup misses, so links and other long-lived references
   * keyed off the legacy SOS IDs continue to resolve against
   * post-cutover ULID-keyed rows.
   */
  legacyId?: string
  /**
   * AYNI — every id the app used for this dataset before (`INTERNAL_SOS_*`
   * from the old S3 list, `SOS_ONLY_*` from NOAA's page export), set on the
   * NOAA library rows from `src/data/noaa-library.json` so old links,
   * Khipus and saved references keep resolving (`resolveDatasetRef`).
   * `legacyId` is the first of them.
   */
  legacyIds?: string[]
  /**
   * URL-safe slug (`sea-ice-extent`, `ssta`) — drives display naming
   * for the Phase 3pg image-sequence frame buttons. The catalog
   * always sets a slug; older SOS-source rows may omit it.
   */
  slug?: string
  title: string
  format: DatasetFormat
  dataLink: string
  organization?: string
  abstractTxt?: string
  thumbnailLink?: string
  legendLink?: string
  /** Color-ramp image used by interactive probing — distinct from
   * legendLink in ~2 of 14 rows where both are present (legendLink
   * is the UI-visible swatch, colorTableLink is the canonical
   * gradient). Phase 3b restored this from the SOS snapshot. */
  colorTableLink?: string
  tags?: string[]

  // Temporal metadata
  startTime?: string  // ISO 8601
  endTime?: string    // ISO 8601
  period?: string     // ISO 8601 duration

  // Other metadata
  isHidden?: boolean
  weight?: number
  closedCaptionLink?: string
  websiteLink?: string
  /**
   * AYNI (2026-10-07): this row's Khipu, from NOAA's guided sequence
   * for it (`/api/stories`): `opening` (how it opens) or `story`
   * (played in the Khipu player).
   */
  khipu?: 'opening' | 'story'

  /** Pixel-coords → data-value mapping for the color table, used
   * by SOS desktop's hover-to-probe feature. Stored as a JSON
   * object in D1 (`probing_info` column) and serialized verbatim;
   * SPA consumption is deferred to a later phase. */
  probingInfo?: ProbingInfo

  /** Geographic bounding box (NSWE in degrees) for the dataset's
   * spatial extent. Phase 3d promoted from `bounding_variables`
   * JSON to typed columns.
   *
   * **Defaults to worldwide at the SPA layer** when the wire
   * shape carries no bbox (see `wireToDataset` /
   * `synthesizeSosOnlyDatasets` in `dataService.ts`). Wire-side
   * the field is still optional — D1's `bbox_*` columns are
   * NULL for the majority of rows today — but every Dataset
   * record handed to UI code carries a populated bbox so the
   * Phase 4 §6.9 Map view can show every dataset's spatial
   * extent without a "missing" branch. Publishers should set
   * a regional bbox when applicable; the default acknowledges
   * that the SOS catalog is overwhelmingly global today.
   *
   * A worldwide box (`{n:90, s:-90, w:-180, e:180}`) is the
   * "global" sentinel the Map view's hide-globals toggle keys
   * off; the SPA's regional-projection feature also
   * short-circuits at render time when it sees worldwide. */
  boundingBox?: { n: number; s: number; w: number; e: number }

  /** Celestial body the dataset visualises. Omitted == Earth.
   * Non-Earth values (Mars / Moon / Sun / Jupiter / …) cue the
   * SPA's Phase 3e base-texture swap. */
  celestialBody?: string

  /** Radius of the celestial body in miles, when non-Earth. */
  radiusMi?: number

  /** Realtime/forecast DASH streams from the Cloudflare R2 catalog;
   * set by `fetchRealtimeDashDatasets` so the UI can tag/prefix them.
   * AYNI: `projection` is a scenario run to a future date (the CMIP6
   * temperature projections, 1950–2100) — neither live nor a forecast.
   * `reanalysis` and `historical` are past records (ERA5 from 1940,
   * satellite sea level), titled by that type rather than "Real Time". */
  realtimeKind?: 'real-time' | 'forecast' | 'projection' | 'reanalysis' | 'historical'

  /** AYNI — a stream row's stable index `name` (`cmip6_tas_anomaly_ssp126`), which survives title renames. */
  streamName?: string

  /**
   * AYNI (owner, 2026-10-30, decision C9) — a NOAA library row's stream
   * twin, by its index `name`: the row is hidden where the viewer's plan
   * plays that stream and shown where it does not (`libraryTwins.ts`).
   */
  libraryTwin?: string

  /**
   * Resolved URL of the stream's `.dsa` annotation — the file beside the
   * MPD that declares the time axis (origin, cadence, frame count) and the
   * provenance of frames the publisher filled in. Set for realtime and
   * forecast rows only. The VR date track reads it; its absence is what
   * makes a dataset show no track at all. See `services/dsaTimeline.ts`.
   */
  timelineLink?: string

  /**
   * AYNI — a still picture from the hosted index (catalog 1.5 `type:
   * "image"`): `dataLink` is the picture and `timelineLink` its `.dsa`
   * (type "image", no time axis), which may add a colour key, a body and
   * vector layers on their own clock. See `services/dataService.ts`.
   */
  stillPicture?: true

  /**
   * AYNI — set only on a release row. `releaseDescriptorLink` is the
   * `latest.json` pointer the loader follows to the current release;
   * `releaseEncoding` is that release's encoding (none for a plain
   * picture release such as GeoColor), filled in at load, which gives both globes its palette, crop and decoding rules
   * (overlayOptionsFromDataset). See `services/dashRelease.ts`.
   */
  releaseDescriptorLink?: string
  /**
   * AYNI — a picture stream's colour key (DSA `alphaMode: "color_key"`),
   * read from its `.dsa` at load: that colour is drawn transparent, so the
   * globe's own Earth shows through. Absent for every other dataset. See
   * `services/colorKey.ts`.
   */
  colorKey?: ColorKey
  /**
   * AYNI — a global, alpha-channel real-time stream that can be layered
   * over another dataset as its real-time overlay.
   */
  rtOverlayCandidate?: boolean
  /**
   * AYNI — what the stream may be used for (web, broadcast, paid renders)
   * and the credit a render must carry, as the index row copies it from
   * the `.dsa`. Absent until the producers write it. See `services/rights.ts`.
   */
  rights?: import('../services/rights').StreamRights
  releaseEncoding?: import('./release-encoding').ReleaseEncoding
  /**
   * AYNI — own data: the account's own private dataset, played through the
   * delivery Worker with a token (`services/privateData.ts`). Never in the
   * catalogue; no Khipu, download or related datasets.
   */
  ownData?: true
  /**
   * AYNI — the shared colour standard the index puts this row in (schema
   * 1.4 `displayGroup`), already checked: the dataset opens with its
   * palette and limits. See `services/displayGroups.ts`.
   */
  displayGroup?: import('../services/displayGroups').DisplayGroup

  /** When true, country/region borders render on by default for this
   * dataset (sparse transparent DASH overlays). */
  defaultBordersVisible?: boolean

  /**
   * AYNI — never place a basemap or overlay on this dataset
   * automatically (owner's rule, 2026-10-02): the real-time Sun streams,
   * whose opaque pictures would otherwise get the borders.
   * A viewer can still tick a layer by hand.
   */
  autoLayersOff?: boolean

  /** Globe longitude rotation reference in degrees. Omitted == 0
   * (prime-meridian-centered). Non-zero values (±180 in the SOS
   * snapshot) are dateline-centered, useful for Pacific-focused
   * datasets. */
  lonOrigin?: number

  /** Image Y-axis flip flag for datasets whose imagery uses
   * inverted Y conventions. Omitted == false. */
  isFlippedInY?: boolean

  /**
   * How many source frames this dataset advances per second.
   *
   * A property of the dataset, not of the file: the rate is already
   * baked in by the time anything plays it, either by holding each
   * frame across a 30 fps container (what the transcode does) or by
   * encoding at this container rate directly (what a hand-encoded
   * data video does). Both arrive playing correctly at `playbackRate
   * = 1`, which is why nothing applies this on load.
   *
   * What it *is* needed for is a request for a rate in dataset frames
   * per second (once a guided sequence's), which can only convert that
   * to a `playbackRate` if it knows what the dataset already advances
   * at. Omitted means 30 — one source frame per output frame, which
   * is every dataset that never set it.
   */
  playbackFps?: number

  /** How the frames encode their pixels. Omitted == a picture
   * (colourised upstream, rendered as-is) — the state of every
   * dataset published before this field existed, and the whole of
   * the backwards-compatibility contract. `'data-luma'` means luma
   * carries the normalised value; `colorScale` says what it means.
   * See `docs/DATA_ENCODED_VIDEO_PLAN.md`. */
  renderEncoding?: RenderEncoding

  /** Palette + scale for a `data-luma` dataset. Present only
   * alongside `renderEncoding`; both are dropped together if
   * either is missing or the sidecar fails to parse. */
  colorScale?: DisplayColorScale

  // Enriched metadata: AYNI — NOAA's catalogue text and credits, merged into
  // the NOAA library index at build time (scripts/build-noaa-library.ts).
  enriched?: EnrichedMetadata

  /**
   * Image-sequence frame envelope — set only for rows that were
   * transcoded from a frames upload (Phase 3pg/A). Carries the
   * frame count, a per-frame URL template (`{index}` is the token
   * consumers substitute with the zero-padded 5-digit frame
   * number), and the publisher-signed `framesDigest` of the
   * canonical source-filenames blob. Older clients ignore this
   * field; sequence rows still play as a regular HLS video via
   * `dataLink`.
   */
  frames?: DatasetFrames
}

/**
 * Image-sequence frame envelope on `Dataset` (Phase 3pg/A). Mirrors
 * `WireDatasetFrames` from `functions/api/v1/_lib/dataset-serializer.ts`.
 * Consumers compute frame N's timestamp as
 * `startTime + period × index` for time-series rows, and render
 * display names as `{slug}_{YYYYMMDDTHHMMSSZ}.{ext}` (time-series)
 * or `{slug}_frame_{NNNNN}.{ext}` (pure-sequence) — same
 * convention the `/api/v1/datasets/{id}/frames` endpoint
 * server-renders for `displayName`.
 */
export interface DatasetFrames {
  count: number
  urlTemplate: string
  framesDigest?: string
}

/** Probing metadata recovered from the SOS snapshot. Pixel
 * positions (`minPos` / `maxPos`) reference coordinates on the
 * color table image; sampling the rendered pixel and interpolating
 * between min and max gives the data value at that screen point.
 *
 * Stored on `datasets.probing_info` as a JSON-stringified blob.
 * Write-side validation (`validateJsonStringField` in
 * `functions/api/v1/_lib/validators.ts`) only confirms the value
 * is a JSON-parseable string under the 4096-char cap — it does
 * NOT enforce this specific object shape. The shape declared
 * here is documentation of the SOS snapshot's payload, not a
 * runtime contract on what consumers will see. A downstream
 * consumer should treat each field as optional and pick the
 * ones it needs.
 */
export interface ProbingInfo {
  units?: string
  minVal?: number
  maxVal?: number
  minPos?: { x?: number; y?: number; XUnits?: string; YUnits?: string }
  maxPos?: { x?: number; y?: number; XUnits?: string; YUnits?: string }
}

/**
 * Rich metadata from the SOS catalog, cross-referenced by title
 */
export interface EnrichedMetadata {
  description?: string
  categories?: Record<string, string[]>
  keywords?: string[]
  relatedDatasets?: Array<{ title: string; url: string }>
  datasetDeveloper?: { name?: string; affiliationUrl?: string }
  visDeveloper?: { name?: string; affiliationUrl?: string }
  dateAdded?: string
  catalogUrl?: string
}

/**
 * Metadata for all available datasets
 */
export interface DatasetMetadata {
  datasets: Dataset[]
}

/**
 * Processed time information
 */
export interface TimeInfo {
  startTime?: Date
  endTime?: Date
  period?: {
    type: 'day' | 'week' | 'month' | 'year' | 'hour' | 'minute' | 'custom'
    days: number
  }
  hasTemporalData: boolean
  displayMode: 'temporal' | 'static' | 'unknown'
}

/**
 * Video proxy service response
 */
export interface VideoProxyResponse {
  id: string
  title: string
  duration: number
  hls?: string
  dash?: string
  files: Array<{
    quality: string
    width?: number
    height?: number
    size: number
    type: string
    link: string
  }>
}

/**
 * Application state
 */
export interface AppState {
  datasets: Dataset[]
  currentDataset: Dataset | null
  isLoading: boolean
  error: string | null
  timeLabel: string
  isPlaying: boolean
  currentFrame: number
  totalFrames: number
}

/**
 * Structured map view context passed to the LLM system prompt.
 */
export interface MapViewContext {
  center: { lat: number; lng: number }
  zoom: number
  bearing: number
  pitch: number
  bounds: { west: number; south: number; east: number; north: number }
  visibleCountries: string[]
  visibleOceans: string[]
}

/**
 * Lightweight handle for a video texture, used by the playback controller
 * to flag manual updates and clean up resources.
 */
export interface VideoTextureHandle {
  needsUpdate: boolean
  dispose(): void
}

/**
 * Per-dataset hints for the renderer's dataset-overlay pass. Phase
 * 3e plumbs these through from `Dataset` (which sources them from
 * the catalog row's Phase 3d metadata) into the WebGL shader so
 * regional / non-Earth / dateline-centered datasets project
 * correctly rather than stretching equirectangularly across the
 * whole sphere.
 *
 *   boundingBox    — when all four corners are present, the shader
 *                    clips the texture to this region and lets
 *                    base tiles show outside it. Omitted → full
 *                    equirectangular projection (legacy behavior).
 *   lonOrigin      — degrees offset for the U axis (default 0 →
 *                    prime-meridian centered; ±180 = dateline
 *                    centered). Applies to the full-globe path
 *                    only — see note below.
 *   isFlippedInY   — if true, the shader samples the texture with
 *                    a flipped V axis (datasets authored with
 *                    inverted-Y conventions).
 *   celestialBody  — non-Earth bodies cue the MapRenderer to swap
 *                    the base raster source and skip the Earth
 *                    4-pass effects (day/night terminator etc.,
 *                    which assume Earth's sun model).
 *
 * Every field is optional. Combinations honored: `bbox` alone,
 * `lonOrigin` alone, `isFlippedInY` with either, `celestialBody`
 * with any of the above. Combinations NOT honored: `bbox` +
 * non-zero `lonOrigin` — the shader's bbox path ignores
 * `uLonOrigin` because the texture is already remapped to the
 * bbox extent. No catalog row combines the two today; if a
 * future publisher needs both, the shader has to be extended
 * (apply `uLonOrigin` to `lon` before the bbox clip/remap)
 * rather than relying on this type to permit it. */
export interface DatasetOverlayOptions {
  boundingBox?: { n: number; s: number; w: number; e: number }
  lonOrigin?: number
  isFlippedInY?: boolean
  celestialBody?: string
  /** Present only for a data-encoded dataset, where the texture's
   *  luma is the normalised value rather than a colour. The renderer
   *  builds a 256×1 LUT from this and colours in the shader; absent
   *  means the texture is already a picture and is drawn as-is.
   *  This is the field that carries data-encoded mode to all four
   *  render surfaces. */
  colorScale?: DisplayColorScale
  /** True when the source media carries its own alpha channel —
   *  the realtime / forecast overlay streams (`application/dash+xml`,
   *  `realtimeKind` set). Those are sparse transparent overlays
   *  (precipitation, smoke, tracks) sampled straight-alpha, so the
   *  renderer must composite them rather than draw them opaque.
   *
   *  Absent for every picture dataset, which is what keeps the
   *  ordinary picture path on the exact GL state it has always had —
   *  blending is enabled *only* for a data-encoded LUT or for one of
   *  these streams. */
  hasAlphaStream?: boolean
  /**
   * AYNI — a picture stream's colour key (services/colorKey.ts): pixels
   * near that colour are drawn transparent. Absent for every other dataset,
   * which keeps their bundle and shaders exactly as before.
   */
  colorKey?: ColorKey
  /**
   * AYNI — the rectangle of the frame that holds the map, in image space
   * (v == 0 is the top row): a value-encoded release carries a
   * calibration strip below its map. Absent means the whole frame.
   */
  cropRect?: { u0: number; v0: number; us: number; vs: number }
  /**
   * AYNI — a value-encoded release's own code layout, so a value readout
   * decodes exactly as the palette does (log and classified kinds are
   * not linear). Plain data, so the bundle stays cloneable.
   */
  releaseEncoding?: import('./release-encoding').ReleaseEncoding
  /** Which dataset these options were built from.
   *
   *  Carried so a *frame* can say what it is, rather than a reader
   *  having to ask app state and hope the two agree. `appState
   *  .currentDataset` and the primary renderer's texture are separate
   *  facts: a 2/4-globe layout, a Khipu switching panels, or a load
   *  landing between them can leave one describing a dataset the other
   *  is not showing. Anything that reports numbers has to attribute
   *  them to the dataset they were actually measured from. */
  datasetId?: string
  datasetTitle?: string
}

/**
 * Globe renderer interface used by modules like datasetLoader that interact
 * with the MapLibre-based globe.
 */
export interface GlobeRenderer {
  updateTexture(texture: HTMLCanvasElement | HTMLImageElement, options?: DatasetOverlayOptions): void
  setVideoTexture(video: HTMLVideoElement, options?: DatasetOverlayOptions): VideoTextureHandle
  flyTo(lat: number, lon: number, altitude?: number): void | Promise<void>
  toggleAutoRotate(): boolean
  setLatLngCallbacks(
    onUpdate: (lat: number, lng: number) => void,
    onClear: () => void,
  ): void
  setCanvasDescription(text: string): void
  loadDefaultEarthMaterials(onProgress?: (fraction: number) => void): Promise<void>
  removeNightLights(): void
  enableSunLighting(lat: number, lng: number): void
  disableSunLighting(): void
  loadCloudOverlay(url: string, onProgress?: (fraction: number) => void): Promise<void>
  removeCloudOverlay(): void
  dispose(): void

  // Optional methods (checked at runtime)
  toggleLabels?(visible?: boolean): boolean
  toggleBoundaries?(visible?: boolean): boolean
  addMarker?(lat: number, lng: number, label?: string, color?: string): unknown
  clearMarkers?(): void
  setRotationRate?(rate: number): void
  getMap?(): unknown
}

/**
 * Kind of general feedback — bug report, feature request, or other.
 */
export type GeneralFeedbackKind = 'bug' | 'feature' | 'other'

/**
 * Payload submitted to /api/general-feedback for app-level feedback
 * (bug reports, feature requests).
 */
export interface GeneralFeedbackPayload {
  kind: GeneralFeedbackKind
  /** User-written description (required, capped server-side at 2000 chars) */
  message: string
  /** Optional email or handle for follow-up */
  contact?: string
  /** window.location.href at submit time */
  url?: string
  /** App version from build metadata */
  appVersion?: string
  /** Always 'web' since the desktop app went (2026-10-06); 'desktop'
   *  (TerraViz's Tauri app) stays for rows stored before. */
  platform?: 'web' | 'desktop'
  /** Currently loaded dataset ID, if any */
  datasetId?: string | null
  /** Optional base64 JPEG data URL from screenshotService */
  screenshot?: string
}

/**
 * In-memory cache for the active dataset's legend image and LLM-generated text description.
 * Populated by initLegendForDataset(); cleared by clearLegendCache() on dataset change.
 */
export interface LegendCache {
  legendBase64: string | null        // full data URL (data:<mime>;base64,...)
  legendMimeType: string | null
  legendDescription: string | null   // LLM-generated text description (non-vision fallback)
  legendDescriptionForDatasetId: string | null
}

// ─────────────────────────────────────────────────────────────────────
// Telemetry
//
// Shape of the analytics event stream. Shared between the client
// emitter (src/analytics/) and the Pages Function at
// functions/api/ingest.ts — both sides validate against the same
// discriminated union.
//
// Design, two-tier model, wiring, and privacy posture are documented
// in docs/ANALYTICS_IMPLEMENTATION_PLAN.md and docs/PRIVACY.md.
// ─────────────────────────────────────────────────────────────────────

/** Telemetry consent level. `off` emits nothing; `essential` emits
 * Tier A events only; `research` emits A + B. User-controllable via
 * the Tools → Privacy panel. */
export type TelemetryTier = 'off' | 'essential' | 'research'

/** Persisted telemetry preferences. Stored in localStorage under
 * `sos-telemetry-config`. `sessionId` is *not* part of this shape —
 * it lives in memory only and rotates on every launch. */
export interface TelemetryConfig {
  tier: TelemetryTier
}

/** Event types that require Tier B (research). Every other event type
 * is Tier A. Kept as a const tuple so the runtime Set and the
 * compile-time `TierBEventType` literal stay in sync. */
export const TIER_B_EVENT_TYPES = [
  'dwell',
  'browse_search',
  'catalog_graph_node_clicked',
  'catalog_timeline_brush_applied',
  'catalog_map_region_drawn',
  'vr_interaction',
  'error_detail',
] as const
export type TierBEventType = (typeof TIER_B_EVENT_TYPES)[number]

// --- Shared enums ---

/** AYNI: always `web` since 2026-10-06; `desktop` / `mobile` were
 *  TerraViz's Tauri apps and stay so stored rows still type-check. */
export type Platform = 'web' | 'desktop' | 'mobile'
export type OsFamily = 'mac' | 'windows' | 'linux' | 'ios' | 'android' | 'unknown'
export type ViewportClass = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
/** Aspect-ratio bucket derived from `window.innerWidth / window.innerHeight`.
 * Low cardinality on purpose — exact dimensions would be a
 * fingerprinting signal. See docs/ANALYTICS_IMPLEMENTATION_PLAN.md
 * "Privacy posture". */
export type AspectClass = 'portrait-tall' | 'portrait' | 'square' | 'landscape' | 'wide' | 'ultrawide'
/** Physical-display bucket derived from `screen.width` — independent
 * from the browser viewport (which is captured by `viewport_class`). */
export type ScreenClass = 'mobile' | 'tablet' | '1080p' | '2k' | '4k+'
/** Build lineage. Server-stamped `environment` (`production` /
 * `preview` / `local`) indicates *where* the app ran; `build_channel`
 * indicates *which audience* the bundle was shipped for. An internal
 * staff build deployed to production still reports
 * `environment='production'` but `build_channel='internal'`. */
export type BuildChannel = 'public' | 'internal' | 'canary'
export type VrCapability = 'none' | 'vr' | 'ar' | 'both'
export type LayerSource = 'network' | 'cache' | 'hls' | 'dash' | 'image'
export type LoadTrigger = 'browse' | 'orbit' | 'url' | 'default'
export type UnloadReason = 'replaced' | 'home' | 'manual'
export type FeedbackContext = 'general' | 'ai_response'
export type FeedbackKind = 'bug' | 'feature' | 'other' | 'thumbs_up' | 'thumbs_down'
export type FeedbackStatus = 'ok' | 'error'
export type DwellTarget = `dataset:${string}` | 'chat' | 'info_panel' | 'browse' | 'tools'
export type VrMode = 'ar' | 'vr'
export type VrExitReason = 'user' | 'error' | 'session_lost'
export type VrGesture = 'drag' | 'pinch' | 'thumbstick_zoom' | 'flick_spin' | 'hud_tap'
/** Specular-strength presets surfaced in Tools → Display (§7.2). */
export type SpecularPreset = 'none' | 'default' | 'comfortable'
export type ErrorCategory =
  | 'tile' | 'hls' | 'llm' | 'download' | 'vr' | 'caption'
  // `native_panic` / `tauri_panic` came from the Tauri app (removed
  // 2026-10-06); kept for stored rows.
  | 'uncaught' | 'console' | 'native_panic'
export type ErrorSource =
  | 'caught' | 'window_error' | 'unhandledrejection'
  | 'console_error' | 'console_warn' | 'tauri_panic'

// --- Base event shape ---

/** Fields every event carries. `client_offset_ms` is stamped by the
 * emitter at enqueue time (not by call sites) and measures time from
 * session start in milliseconds. */
export interface TelemetryEventBase {
  client_offset_ms?: number
}

// --- Tier A events ---

export interface SessionStartEvent extends TelemetryEventBase {
  event_type: 'session_start'
  app_version: string
  /** Shell type — `'web'` (a browser tab) on AYNI. `'desktop'` and
   * `'mobile'` were TerraViz's Tauri apps, removed 2026-10-06. */
  platform: Platform
  /** OS family — never version. Bucketed to six values to avoid
   * fingerprinting. */
  os: OsFamily
  locale: string
  /** Browser viewport bucket (innerWidth-derived). */
  viewport_class: ViewportClass
  /** Browser viewport aspect ratio bucket. Captures orientation
   * alongside shape — portrait phone vs ultrawide monitor etc. */
  aspect_class: AspectClass
  /** Physical-display bucket (screen.width-derived). Independent
   * from viewport because a user on a 4K monitor may resize the
   * browser to a 1080p window. */
  screen_class: ScreenClass
  /** Build audience — `'public'` unless the bundle was produced
   * with `VITE_BUILD_CHANNEL=internal` (staff dogfood) or
   * `VITE_BUILD_CHANNEL=canary` (staged rollout). */
  build_channel: BuildChannel
  vr_capable: VrCapability
  schema_version: string
  /** True when this is a re-start after the user switched telemetry
   * back on mid-app-lifetime (not a fresh app boot). */
  resumed?: boolean
}

export interface SessionEndEvent extends TelemetryEventBase {
  event_type: 'session_end'
  exit_reason: 'pagehide' | 'visibilitychange' | 'clean'
  duration_ms: number
  event_count: number
  /** Page-visible wall-clock ms — the idle-tab-aware view time.
   * Accumulated across `visibilitychange` transitions, so a tab
   * opened in the background reports ~0 while `duration_ms` keeps
   * counting. Field name chosen to sort after `event_count`
   * alphabetically so existing AE double positions stay stable
   * (the ANALYTICS_CONTRIBUTING.md positional rule); rows from
   * clients predating this field read back as 0 = unknown. */
  visible_ms: number
}

export interface LayerLoadedEvent extends TelemetryEventBase {
  event_type: 'layer_loaded'
  layer_id: string
  layer_source: LayerSource
  slot_index: string
  trigger: LoadTrigger
  load_ms: number
}

export interface LayerUnloadedEvent extends TelemetryEventBase {
  event_type: 'layer_unloaded'
  layer_id: string
  slot_index: string
  reason: UnloadReason
  dwell_ms: number
}

export interface FeedbackEvent extends TelemetryEventBase {
  event_type: 'feedback'
  context: FeedbackContext
  kind: FeedbackKind
  status: FeedbackStatus
  /** −1 / 0 / +1 */
  rating: -1 | 0 | 1
}

export interface CameraSettledEvent extends TelemetryEventBase {
  event_type: 'camera_settled'
  slot_index: string
  /** Projection the user was viewing when the camera settled.
   * `globe` is the MapLibre globe; `vr` is an
   * immersive-vr session; `ar` is an immersive-ar session. AYNI
   * (2026-10-03): no flat-map value — Browse's Map view is Equal Earth
   * on its own canvas and reports no camera; upstream's Mercator value
   * is gone with it. VR/AR
   * events report the lat/lon under the user's gaze ray onto the
   * globe and a scale-derived `zoom`; bearing/pitch describe the
   * head-to-globe orientation. */
  projection: 'globe' | 'vr' | 'ar'
  center_lat: number
  center_lon: number
  zoom: number
  bearing: number
  pitch: number
  /** Dataset currently loaded in the slot at the moment the camera
   * settled. Empty string when the panel is showing the default
   * Earth. Required and non-null so blob positions stay stable in
   * Analytics Engine — see comment on `toDataPoint`. */
  layer_id: string
}

export interface MapClickEvent extends TelemetryEventBase {
  event_type: 'map_click'
  slot_index: string
  hit_kind: 'surface' | 'marker' | 'feature' | 'region'
  /** Empty string when the click landed on a bare surface with no
   * marker / feature / region id. See `toDataPoint` for why we
   * avoid `null` on the wire. */
  hit_id: string
  lat: number
  lon: number
  zoom: number
}

export interface ViewportFocusEvent extends TelemetryEventBase {
  event_type: 'viewport_focus'
  slot_index: string
  layout: '1globe' | '2globes' | '4globes'
}

export interface LayoutChangedEvent extends TelemetryEventBase {
  event_type: 'layout_changed'
  layout: '1globe' | '2globes' | '4globes'
  trigger: 'tools' | 'orbit'
}

export interface PlaybackActionEvent extends TelemetryEventBase {
  event_type: 'playback_action'
  layer_id: string
  action: 'play' | 'pause' | 'seek' | 'rate'
  playback_time_s: number
  playback_rate: number
}

export interface SettingsChangedEvent extends TelemetryEventBase {
  event_type: 'settings_changed'
  key: string
  value_class: string
}

export interface BrowseOpenedEvent extends TelemetryEventBase {
  event_type: 'browse_opened'
  source: 'tools' | 'orbit' | 'shortcut'
}

export interface BrowseFilterEvent extends TelemetryEventBase {
  event_type: 'browse_filter'
  category: string
  result_count_bucket: '0' | '1-10' | '11-50' | '50+'
}

/**
 * Catalog view-mode toggle (Phase 4 §6.7+). Fires when the user
 * switches the browse overlay between the card grid, the network
 * graph, the upcoming Timeline view (§6.8), and the upcoming Map
 * view (§6.9). Tier A — the choice is a pure UI preference and
 * carries no free-text payload. `from` records what the user just
 * came from so the dashboard can read both stickiness and
 * direction of pivots.
 */
export interface CatalogViewModeChangedEvent extends TelemetryEventBase {
  event_type: 'catalog_view_mode_changed'
  view_mode: 'cards' | 'graph' | 'timeline' | 'map'
  from: 'cards' | 'graph' | 'timeline' | 'map'
  /** Bucketed dataset count visible at the moment of toggle — useful
   *  for "did the user pivot to Graph because Cards was overwhelming?". */
  result_count_bucket: '0' | '1-10' | '11-50' | '50+'
}

export interface VrSessionStartedEvent extends TelemetryEventBase {
  event_type: 'vr_session_started'
  mode: VrMode
  device_class: string
  entry_load_ms: number
  /** Dataset loaded in the primary panel at the moment the user
   * entered VR. Empty string when entering with the default Earth
   * view. Snapshot — a load that happens later in the session is
   * captured separately by the next `layer_loaded` event. */
  layer_id: string
}

export interface VrSessionEndedEvent extends TelemetryEventBase {
  event_type: 'vr_session_ended'
  mode: VrMode
  exit_reason: VrExitReason
  duration_ms: number
  /** End-of-session arithmetic mean of FPS over the whole session
   * (`total frames / wall-clock duration`). For per-window medians
   * during the session, see `perf_sample.fps_median_10s`. `0` when
   * the session was too short for a meaningful sample (< 1 s) —
   * dashboards filter `mean_fps > 0` to exclude these. */
  mean_fps: number
  /** Dataset loaded in the primary panel at the moment the session
   * ended. May differ from `vr_session_started.layer_id` if the
   * user loaded a different dataset mid-session. Empty string
   * when the session ended on the default Earth view. */
  layer_id: string
}

export interface VrPlacementEvent extends TelemetryEventBase {
  event_type: 'vr_placement'
  /** Empty string when no dataset was loaded at placement time. */
  layer_id: string
  persisted: boolean
}

export interface PerfSampleEvent extends TelemetryEventBase {
  event_type: 'perf_sample'
  surface: 'map' | 'vr'
  /** SHA-256 of WEBGL_debug_renderer_info, first 8 hex chars. */
  webgl_renderer_hash: string
  fps_median_10s: number
  frame_time_p95_ms: number
  /** `0` when `performance.memory` is unavailable (non-Chromium).
   * Dashboards filter `jsheap_mb > 0` to exclude unsupported
   * browsers from the distribution. */
  jsheap_mb: number
}

export interface ErrorEvent extends TelemetryEventBase {
  event_type: 'error'
  category: ErrorCategory
  source: ErrorSource
  /** HTTP status or classified enum value. */
  code: string
  /** Sanitized first line of the error message, <= 80 chars. URLs,
   * emails, UUIDs, digit runs and file paths stripped at the
   * sanitizer before emission. */
  message_class: string
  /** For deduped repeats of the same signature within a batch. */
  count_in_batch: number
}

// --- Tier B events ---

export interface DwellEvent extends TelemetryEventBase {
  event_type: 'dwell'
  view_target: DwellTarget
  duration_ms: number
}

export interface BrowseSearchEvent extends TelemetryEventBase {
  event_type: 'browse_search'
  /** Client-side SHA-256 of the lowercased query, first 12 hex. */
  query_hash: string
  result_count_bucket: '0' | '1-10' | '11-50' | '50+'
  query_length: number
}

/**
 * Catalog Graph view node interaction (Phase 4 §6.7). Fires when
 * the user clicks a node in the Graph view. Tier B — node values
 * (Category names, keyword values, dataset IDs) are free-text by
 * the privacy posture's definition, so `value_hash` carries a
 * SHA-256 prefix rather than the value itself. Throttled to
 * ≤30/min via the same rolling-window pattern as `camera_settled`
 * so an aggressive panning session can't flood the queue.
 *
 * `node_kind` and `facet` are low-cardinality enums (3 × ~10
 * facets) so they're safe to emit verbatim — they tell the
 * dashboard "user clicked a Category facet-value node" without
 * revealing which one.
 */
export interface CatalogGraphNodeClickedEvent extends TelemetryEventBase {
  event_type: 'catalog_graph_node_clicked'
  node_kind: 'facet-value' | 'keyword' | 'dataset'
  /** Facet name for facet-value nodes; `'keyword'` for keyword
   *  nodes; `''` for dataset nodes. */
  facet: string
  /** First 12 hex of SHA-256 of the lowercased value (facet value /
   *  keyword / dataset id). Privacy-friendly count of distinct
   *  clicks without exposing the value itself. */
  value_hash: string
}

/**
 * Catalog Timeline view brush gesture (Phase 4 §6.8). Fires when
 * the user commits a brush selection on the time axis, which
 * writes a `dataCoverageYear` range predicate via the same
 * `setFacet` mutation path the chip rail's range inputs use.
 * Tier B because — like Graph node clicks — it captures a
 * filter-shaping signal that's deeper than the chip rail's
 * coarse "user filtered" event, and the dashboard's question
 * here is investigative ("which date ranges do users actually
 * brush?") rather than operator-critical.
 *
 * Throttled to ≤30 / minute per session by the rolling-window
 * pattern in `src/analytics/camera.ts`, same shared budget as
 * `catalog_graph_node_clicked`. Payload is integers only — the
 * brush carries no free text, so no `*_hash` field is needed.
 */
export interface CatalogTimelineBrushAppliedEvent extends TelemetryEventBase {
  event_type: 'catalog_timeline_brush_applied'
  /** Inclusive start year of the brushed range. */
  start_year: number
  /** Inclusive end year of the brushed range. */
  end_year: number
}

/**
 * Catalog Map view draw-rectangle gesture (Phase 4 §6.9). Fires
 * when the user commits a region selection on the Equal Earth map,
 * which writes a `geographicRegion` bbox predicate via the same
 * `setFacet` mutation path the chip rail's range inputs and the
 * Timeline brush both use. Tier B because — like the Graph node
 * click and the Timeline brush — it captures a filter-shaping
 * signal deeper than the chip rail's coarse "user filtered" event,
 * and the dashboard's question here is investigative ("which
 * regions do users actually draw?") rather than operator-critical.
 *
 * Throttled to ≤30 / minute per session by the rolling-window
 * pattern in `src/analytics/camera.ts`, same shared budget as
 * `catalog_graph_node_clicked` and `catalog_timeline_brush_applied`.
 * Bounds round to 3 decimals (~111 m at the equator) — same
 * precision `camera.ts` uses for lat/lon — so the analytics
 * surface never leaks high-resolution drag positions.
 */
export interface CatalogMapRegionDrawnEvent extends TelemetryEventBase {
  event_type: 'catalog_map_region_drawn'
  /** Northernmost latitude of the drawn region, in degrees. */
  north: number
  /** Southernmost latitude of the drawn region, in degrees. */
  south: number
  /** Easternmost longitude of the drawn region, in degrees. */
  east: number
  /** Westernmost longitude of the drawn region, in degrees. */
  west: number
}

export interface VrInteractionEvent extends TelemetryEventBase {
  event_type: 'vr_interaction'
  gesture: VrGesture
  magnitude: number
}

export interface ErrorDetailEvent extends TelemetryEventBase {
  event_type: 'error_detail'
  category: ErrorCategory
  source: ErrorSource
  message_class: string
  /** SHA-256 of normalized stack, first 12 hex. */
  stack_signature: string
  /** Sanitized stack frames — function names only, no URLs or line
   * numbers. Max 10 frames. */
  frames_json: string
  count_in_batch: number
}

/** The full discriminated event union. Add new events here, add them
 * to `TIER_B_EVENT_TYPES` if Tier B, and update the wiring table in
 * `docs/ANALYTICS_IMPLEMENTATION_PLAN.md`. AYNI (2026-10-07): the
 * ingest keeps only the fields listed for each event in `EVENT_FIELDS`
 * (`functions/api/ingest.ts`), which the compiler holds to this union;
 * TerraViz's `migration_r2_*`, `orbit_*` and `voice_interaction` went,
 * as nothing here sends them. */
export type TelemetryEvent =
  // Tier A
  | SessionStartEvent
  | SessionEndEvent
  | LayerLoadedEvent
  | LayerUnloadedEvent
  | FeedbackEvent
  | CameraSettledEvent
  | MapClickEvent
  | ViewportFocusEvent
  | LayoutChangedEvent
  | PlaybackActionEvent
  | SettingsChangedEvent
  | BrowseOpenedEvent
  | BrowseFilterEvent
  | CatalogViewModeChangedEvent
  | VrSessionStartedEvent
  | VrSessionEndedEvent
  | VrPlacementEvent
  | PerfSampleEvent
  | ErrorEvent
  // Tier B
  | DwellEvent
  | BrowseSearchEvent
  | CatalogGraphNodeClickedEvent
  | CatalogTimelineBrushAppliedEvent
  | CatalogMapRegionDrawnEvent
  | VrInteractionEvent
  | ErrorDetailEvent
